/**
 * Author: Claude Code
 * Version: v3.0.84
 * Modified: 2025-09-09 Beijing Time
 * 
 * AgentRegistrationManager - Agent注册和初始化系统
 * 负责Agent配置管理、验证和spawn分布算法
 */

import webSocketService from '../../services/websocket/WebSocketService';

export interface AgentConfiguration {
  // 基础属性 (总和必须等于150点)
  hp: number;           // 生命值 (10-50)
  attack: number;       // 攻击力 (10-50)
  defense: number;      // 防御力 (10-50)
  speed: number;        // 移动速度 (10-50)
  aggressiveness: number; // 攻击性 (10-50)
}

export interface Position {
  x: number;
  y: number;
}

export interface Bounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface AgentRegistration {
  id: string;
  username: string;
  configuration: AgentConfiguration;
  spawnPosition?: Position;
  registrationTime: number;
  isValid: boolean;
}

export class AgentRegistrationManager {
  private registeredAgents: Map<string, AgentRegistration> = new Map();
  private spawnQueue: AgentRegistration[] = [];
  private maxAgents: number = 5; // Default max agents per game
  private totalPoints: number = 150; // Total attribute points available
  
  // Attribute constraints
  private static readonly ATTRIBUTE_LIMITS = {
    min: 10,
    max: 50
  } as const;

  // Map constraints for spawn distribution
  private mapBounds: Bounds = {
    minX: 2,
    maxX: 23,
    minY: 2,
    maxY: 17
  };

  constructor(maxAgents: number = 5) {
    this.maxAgents = maxAgents;
    this.initializeWebSocketListeners();
  }

  // Initialize WebSocket event listeners
  private initializeWebSocketListeners(): void {
    // Listen for agent registration events from backend
    webSocketService.on('agent:registered', (data: any) => {
      this.handleAgentRegistered(data);
    });

    webSocketService.on('agent:spawn_positions', (data: any) => {
      this.handleSpawnPositions(data);
    });

    webSocketService.on('game:start_registration', () => {
      this.startRegistrationPhase();
    });

    webSocketService.on('game:end_registration', () => {
      this.endRegistrationPhase();
    });
  }

  // Register a new agent with configuration
  registerAgent(username: string, configuration: AgentConfiguration): string {
    // Generate unique agent ID
    const agentId = this.generateAgentId();

    // Validate configuration
    const validationResult = this.validateConfiguration(configuration);
    if (!validationResult.isValid) {
      throw new Error(`Invalid agent configuration: ${validationResult.errors.join(', ')}`);
    }

    // Check if registration is full
    if (this.registeredAgents.size >= this.maxAgents) {
      throw new Error(`Registration full. Maximum ${this.maxAgents} agents allowed.`);
    }

    // Create agent registration
    const registration: AgentRegistration = {
      id: agentId,
      username,
      configuration: { ...configuration },
      registrationTime: Date.now(),
      isValid: true
    };

    // Store registration
    this.registeredAgents.set(agentId, registration);
    this.spawnQueue.push(registration);

    // Sync with backend
    this.syncRegistrationWithBackend(registration);

    console.log(`Agent registered: ${username} (${agentId})`);
    console.log('Configuration:', configuration);

    return agentId;
  }

  // Validate agent configuration
  validateConfiguration(config: AgentConfiguration): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const { min, max } = AgentRegistrationManager.ATTRIBUTE_LIMITS;

    // Check individual attribute ranges
    Object.entries(config).forEach(([attr, value]) => {
      if (value < min || value > max) {
        errors.push(`${attr} must be between ${min} and ${max}, got ${value}`);
      }
    });

    // Check total points
    const totalPoints = config.hp + config.attack + config.defense + config.speed + config.aggressiveness;
    if (totalPoints !== this.totalPoints) {
      errors.push(`Total points must equal ${this.totalPoints}, got ${totalPoints}`);
    }

    // Check for integer values
    Object.entries(config).forEach(([attr, value]) => {
      if (!Number.isInteger(value)) {
        errors.push(`${attr} must be an integer, got ${value}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Get registered agent by ID
  getAgent(agentId: string): AgentRegistration | undefined {
    return this.registeredAgents.get(agentId);
  }

  // Get all registered agents
  getAllAgents(): AgentRegistration[] {
    return Array.from(this.registeredAgents.values());
  }

  // Get registration count
  getRegistrationCount(): number {
    return this.registeredAgents.size;
  }

  // Check if registration is full
  isRegistrationFull(): boolean {
    return this.registeredAgents.size >= this.maxAgents;
  }

  // Get remaining slots
  getRemainingSlots(): number {
    return Math.max(0, this.maxAgents - this.registeredAgents.size);
  }

  // Calculate spawn positions using Poisson disk sampling
  calculateSpawnPositions(agentCount: number = this.registeredAgents.size): Position[] {
    if (agentCount === 0) return [];

    try {
      // Validate input parameters
      if (agentCount < 0) {
        throw new Error('Agent count must be non-negative');
      }

      if (agentCount > this.maxAgents) {
        console.warn(`Agent count ${agentCount} exceeds maximum ${this.maxAgents}, clamping to maximum`);
        agentCount = this.maxAgents;
      }

      // Validate map bounds
      if (!this.isValidBounds(this.mapBounds)) {
        throw new Error('Invalid map bounds for spawn calculation');
      }

      const positions = this.poissonDiskSampling(
        this.mapBounds,
        agentCount,
        3.0 // Minimum distance between agents (in grid units)
      );

      if (positions.length < agentCount) {
        console.warn(`Could only generate ${positions.length} positions out of requested ${agentCount}`);
      }

      return positions.slice(0, agentCount);
    } catch (error) {
      console.error('Error calculating spawn positions:', error);
      // Fallback to simple grid positions
      return this.generateFallbackGridPositions(agentCount);
    }
  }

  // Validate map bounds
  private isValidBounds(bounds: Bounds): boolean {
    return bounds.minX < bounds.maxX && 
           bounds.minY < bounds.maxY &&
           Number.isFinite(bounds.minX) &&
           Number.isFinite(bounds.maxX) &&
           Number.isFinite(bounds.minY) &&
           Number.isFinite(bounds.maxY);
  }

  // Generate fallback grid positions if Poisson disk sampling fails
  private generateFallbackGridPositions(count: number): Position[] {
    const positions: Position[] = [];
    const { minX, maxX, minY, maxY } = this.mapBounds;
    
    // Calculate grid spacing
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);
    
    const xSpacing = Math.max(1, Math.floor((maxX - minX) / cols));
    const ySpacing = Math.max(1, Math.floor((maxY - minY) / rows));
    
    for (let i = 0; i < count; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      
      positions.push({
        x: Math.min(maxX, minX + col * xSpacing + Math.floor(xSpacing / 2)),
        y: Math.min(maxY, minY + row * ySpacing + Math.floor(ySpacing / 2))
      });
    }
    
    return positions;
  }

  // Poisson disk sampling algorithm for uniform distribution
  private poissonDiskSampling(bounds: Bounds, numPoints: number, minDistance: number): Position[] {
    const positions: Position[] = [];
    const width = bounds.maxX - bounds.minX;
    const height = bounds.maxY - bounds.minY;
    const cellSize = minDistance / Math.sqrt(2);
    const gridWidth = Math.ceil(width / cellSize);
    const gridHeight = Math.ceil(height / cellSize);
    
    // Grid to track occupied cells
    const grid: (Position | null)[][] = Array(gridWidth).fill(null).map(() => Array(gridHeight).fill(null));
    
    // Active list for processing
    const activeList: Position[] = [];
    
    // Generate first point
    const firstPoint: Position = {
      x: bounds.minX + Math.random() * width,
      y: bounds.minY + Math.random() * height
    };
    
    positions.push(firstPoint);
    activeList.push(firstPoint);
    
    const gridX = Math.floor((firstPoint.x - bounds.minX) / cellSize);
    const gridY = Math.floor((firstPoint.y - bounds.minY) / cellSize);
    if (gridX >= 0 && gridX < gridWidth && gridY >= 0 && gridY < gridHeight) {
      grid[gridX][gridY] = firstPoint;
    }
    
    // Generate remaining points
    while (activeList.length > 0 && positions.length < numPoints) {
      const activeIndex = Math.floor(Math.random() * activeList.length);
      const activePoint = activeList[activeIndex];
      let validPointFound = false;
      
      // Try to generate new points around active point
      for (let attempt = 0; attempt < 30; attempt++) {
        const angle = Math.random() * 2 * Math.PI;
        const radius = minDistance * (1 + Math.random());
        const newPoint: Position = {
          x: activePoint.x + radius * Math.cos(angle),
          y: activePoint.y + radius * Math.sin(angle)
        };
        
        // Check if point is within bounds
        if (newPoint.x < bounds.minX || newPoint.x > bounds.maxX ||
            newPoint.y < bounds.minY || newPoint.y > bounds.maxY) {
          continue;
        }
        
        // Check if point is far enough from existing points
        if (this.isValidPosition(newPoint, grid, cellSize, minDistance, bounds)) {
          positions.push(newPoint);
          activeList.push(newPoint);
          
          const newGridX = Math.floor((newPoint.x - bounds.minX) / cellSize);
          const newGridY = Math.floor((newPoint.y - bounds.minY) / cellSize);
          if (newGridX >= 0 && newGridX < gridWidth && newGridY >= 0 && newGridY < gridHeight) {
            grid[newGridX][newGridY] = newPoint;
          }
          
          validPointFound = true;
          break;
        }
      }
      
      // Remove active point if no valid points found
      if (!validPointFound) {
        activeList.splice(activeIndex, 1);
      }
    }
    
    // If we don't have enough points, fill with fallback positions
    while (positions.length < numPoints) {
      positions.push(this.generateFallbackPosition(bounds, positions, minDistance));
    }
    
    // Round positions to grid coordinates
    return positions.map(pos => ({
      x: Math.round(pos.x),
      y: Math.round(pos.y)
    }));
  }

  // Check if position is valid (not too close to existing points)
  private isValidPosition(
    point: Position,
    grid: (Position | null)[][],
    cellSize: number,
    minDistance: number,
    bounds: Bounds
  ): boolean {
    const gridX = Math.floor((point.x - bounds.minX) / cellSize);
    const gridY = Math.floor((point.y - bounds.minY) / cellSize);
    
    // Check surrounding grid cells
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const checkX = gridX + dx;
        const checkY = gridY + dy;
        
        if (checkX >= 0 && checkX < grid.length && checkY >= 0 && checkY < grid[0].length) {
          const existingPoint = grid[checkX][checkY];
          if (existingPoint) {
            const distance = Math.sqrt(
              Math.pow(point.x - existingPoint.x, 2) + Math.pow(point.y - existingPoint.y, 2)
            );
            if (distance < minDistance) {
              return false;
            }
          }
        }
      }
    }
    
    return true;
  }

  // Generate fallback position when Poisson disk sampling doesn't produce enough points
  private generateFallbackPosition(bounds: Bounds, existingPositions: Position[], minDistance: number): Position {
    let attempts = 0;
    const maxAttempts = 100;
    
    while (attempts < maxAttempts) {
      const candidate: Position = {
        x: Math.round(bounds.minX + Math.random() * (bounds.maxX - bounds.minX)),
        y: Math.round(bounds.minY + Math.random() * (bounds.maxY - bounds.minY))
      };
      
      // Check distance from existing positions
      let validPosition = true;
      for (const existing of existingPositions) {
        const distance = Math.sqrt(
          Math.pow(candidate.x - existing.x, 2) + Math.pow(candidate.y - existing.y, 2)
        );
        if (distance < minDistance) {
          validPosition = false;
          break;
        }
      }
      
      if (validPosition) {
        return candidate;
      }
      
      attempts++;
    }
    
    // Ultimate fallback: return a position even if it's close
    return {
      x: Math.round(bounds.minX + Math.random() * (bounds.maxX - bounds.minX)),
      y: Math.round(bounds.minY + Math.random() * (bounds.maxY - bounds.minY))
    };
  }

  // Assign spawn positions to registered agents
  assignSpawnPositions(): void {
    const agents = Array.from(this.registeredAgents.values());
    const positions = this.calculateSpawnPositions(agents.length);
    
    agents.forEach((agent, index) => {
      if (index < positions.length) {
        agent.spawnPosition = positions[index];
        this.registeredAgents.set(agent.id, agent);
      }
    });
    
    console.log(`Assigned spawn positions to ${agents.length} agents`);
  }

  // Update map bounds (called when map size changes)
  updateMapBounds(bounds: Bounds): void {
    this.mapBounds = { ...bounds };
    console.log('Map bounds updated:', bounds);
  }

  // Generate unique agent ID
  private generateAgentId(): string {
    return `agent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Sync registration with backend
  private async syncRegistrationWithBackend(registration: AgentRegistration): Promise<void> {
    try {
      await webSocketService.emit('agent:register', {
        agentId: registration.id,
        username: registration.username,
        configuration: registration.configuration,
        timestamp: registration.registrationTime
      });
    } catch (error) {
      console.error('Failed to sync agent registration with backend:', error);
    }
  }

  // Handle agent registered event from backend
  private handleAgentRegistered(data: any): void {
    console.log('Agent registration confirmed by backend:', data);
    // Update local state if needed
  }

  // Handle spawn positions from backend
  private handleSpawnPositions(data: any): void {
    if (data.positions && Array.isArray(data.positions)) {
      data.positions.forEach((posData: any) => {
        const agent = this.registeredAgents.get(posData.agentId);
        if (agent) {
          agent.spawnPosition = posData.position;
          this.registeredAgents.set(agent.id, agent);
        }
      });
      console.log('Spawn positions updated from backend');
    }
  }

  // Start registration phase
  private startRegistrationPhase(): void {
    console.log('Agent registration phase started');
    this.registeredAgents.clear();
    this.spawnQueue.length = 0;
  }

  // End registration phase and finalize spawn positions
  private endRegistrationPhase(): void {
    console.log('Agent registration phase ended');
    this.assignSpawnPositions();
  }

  // Clear all registrations (for new game)
  clearRegistrations(): void {
    this.registeredAgents.clear();
    this.spawnQueue.length = 0;
    console.log('All agent registrations cleared');
  }

  // Get registration statistics
  getRegistrationStats(): {
    total: number;
    remaining: number;
    agents: { username: string; configuration: AgentConfiguration }[];
  } {
    const agents = Array.from(this.registeredAgents.values());
    return {
      total: agents.length,
      remaining: this.getRemainingSlots(),
      agents: agents.map(agent => ({
        username: agent.username,
        configuration: agent.configuration
      }))
    };
  }
}

export default AgentRegistrationManager;